// LocationTagDialog — choose how to tag a scan's location:
//   • Use my current location (browser geolocation, fast path)
//   • Enter a ZIP code (forward geocode via /api/geocode/zip)
//
// Rationale: gardeners often review a scan long after leaving the plant,
// so the phone's current position is useless. The ZIP option lets them
// pin the scan to the town where it was photographed. US-only ZIPs for
// now (5-digit numeric) — the backend rejects anything else.
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, MapPin, Hash } from "lucide-react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { plants as plantsApi, geocode as geocodeApi } from "@/lib/api";

export default function LocationTagDialog({ open, onOpenChange, scanId, onSaved }) {
  const { t } = useTranslation();
  const [zip, setZip] = useState("");
  const [busy, setBusy] = useState(null); // null | "gps" | "zip"

  useEffect(() => {
    if (open) {
      setZip("");
      setBusy(null);
    }
  }, [open]);

  const closeIfIdle = (nextOpen) => {
    if (busy) return; // don't close mid-request
    onOpenChange(nextOpen);
  };

  const saveCoords = async (latitude, longitude) => {
    const { data } = await plantsApi.setLocation(scanId, { latitude, longitude });
    onSaved?.(data);
    toast.success(t("result.locationSaved"));
    onOpenChange(false);
  };

  const useCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error(t("result.locationUnsupported"));
      return;
    }
    setBusy("gps");
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          await saveCoords(pos.coords.latitude, pos.coords.longitude);
        } catch {
          toast.error(t("result.locationFail"));
        } finally {
          setBusy(null);
        }
      },
      () => { setBusy(null); toast.error(t("result.locationDenied")); },
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  };

  const submitZip = async (e) => {
    e?.preventDefault?.();
    const normalized = zip.trim();
    if (!/^\d{5}$/.test(normalized)) {
      toast.error(t("result.zipInvalid"));
      return;
    }
    setBusy("zip");
    try {
      const { data } = await geocodeApi.zip(normalized);
      await saveCoords(data.lat, data.lng);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 404) toast.error(t("result.zipNotFound"));
      else if (status === 422) toast.error(t("result.zipInvalid"));
      else toast.error(t("result.locationFail"));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={closeIfIdle}>
      <DialogContent className="max-w-md" data-testid="tag-location-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">
            {t("result.locationDialogTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("result.locationDialogDesc")}
          </DialogDescription>
        </DialogHeader>

        {/* Fast path — the phone knows exactly where the user is right now. */}
        <button
          type="button"
          onClick={useCurrentLocation}
          disabled={!!busy}
          data-testid="tag-location-use-gps"
          className="w-full flex items-center gap-3 rounded-2xl border border-[#E8E5DF] bg-white hover:bg-parchment transition p-4 disabled:opacity-60 text-left"
        >
          <span className="shrink-0 w-10 h-10 rounded-2xl bg-leaf/10 text-leaf flex items-center justify-center">
            {busy === "gps" ? (
              <Loader2 className="w-5 h-5 animate-spin" strokeWidth={1.5} />
            ) : (
              <MapPin className="w-5 h-5" strokeWidth={1.5} />
            )}
          </span>
          <span className="min-w-0 flex-1">
            <span className="block font-display text-base text-leaf-ink">
              {t("result.locationUseGpsTitle")}
            </span>
            <span className="block text-[12px] text-leaf-mist mt-0.5">
              {t("result.locationUseGpsHelp")}
            </span>
          </span>
        </button>

        {/* Manual path — user is reviewing a scan long after leaving the site. */}
        <form
          onSubmit={submitZip}
          className="rounded-2xl border border-[#E8E5DF] bg-white p-4"
          data-testid="tag-location-zip-form"
        >
          <div className="flex items-center gap-3">
            <span className="shrink-0 w-10 h-10 rounded-2xl bg-parchment text-leaf-ink flex items-center justify-center">
              <Hash className="w-5 h-5" strokeWidth={1.5} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="font-display text-base text-leaf-ink">
                {t("result.locationZipTitle")}
              </p>
              <p className="text-[12px] text-leaf-mist mt-0.5">
                {t("result.locationZipHelp")}
              </p>
            </div>
          </div>
          <div className="mt-3 flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{5}"
              value={zip}
              onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
              placeholder={t("result.zipPlaceholder")}
              maxLength={5}
              disabled={!!busy}
              data-testid="tag-location-zip-input"
              className="flex-1 rounded-2xl border border-[#E8E5DF] bg-cream px-4 py-3 text-sm text-leaf-ink focus:outline-none focus:ring-2 focus:ring-leaf/40 tabular-nums"
              aria-label={t("result.zipInputAria")}
            />
            <button
              type="submit"
              disabled={!!busy || !/^\d{5}$/.test(zip)}
              data-testid="tag-location-zip-submit"
              className="rounded-full px-4 py-3 bg-leaf text-white text-sm font-medium hover:bg-leaf-deep transition inline-flex items-center gap-2 disabled:opacity-60"
            >
              {busy === "zip" ? (
                <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} />
              ) : null}
              {t("result.zipSubmit")}
            </button>
          </div>
        </form>

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={!!busy}
            data-testid="tag-location-cancel"
            className="rounded-full px-5 py-2.5 text-sm bg-white border border-[#E8E5DF] text-leaf-ink hover:bg-parchment transition"
          >
            {t("common.cancel")}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
